/**
 * Sistema de Gerenciamento de Permissões
 * Implementação específica para gerenciar permissões web
 */

import { 
  IPermissionService,
  PermissionType,
  PermissionStatus,
  PermissionResult
} from '../interfaces/permission_service';
import { ServiceStatus } from '../interfaces/base_platform_service';

export class PermissionSystem implements IPermissionService {
  readonly serviceName = 'Permission' as const;
  private _isInitialized = false;
  private permissionCache = new Map<PermissionType, PermissionResult>();

  get isInitialized(): boolean {
    return this._isInitialized;
  }

  async initialize(): Promise<void> {
    if (this._isInitialized) return;

    console.log('🔐 Inicializando sistema de permissões...');
    this._isInitialized = true;
    console.log('✅ Sistema de permissões inicializado');
  }

  cleanup(): void {
    this.permissionCache.clear();
    this._isInitialized = false;
  }

  getStatus(): ServiceStatus {
    return {
      isAvailable: true,
      isInitialized: this._isInitialized,
      lastUpdate: new Date(),
      metadata: {
        cachedPermissions: this.permissionCache.size,
        supportedTypes: Object.values(PermissionType).length
      }
    };
  }

  async checkPermission(type: PermissionType): Promise<PermissionResult> {
    // Verifica cache primeiro
    const cached = this.permissionCache.get(type);
    if (cached && Date.now() - cached.timestamp.getTime() < 30000) { // 30s cache
      return cached;
    }

    const result = await this.queryPermission(type);
    this.permissionCache.set(type, result);
    return result;
  }

  async requestPermission(type: PermissionType): Promise<PermissionResult> {
    const currentStatus = await this.checkPermission(type);
    
    if (!currentStatus.isSupported) {
      return currentStatus;
    }

    if (currentStatus.status === PermissionStatus.GRANTED) {
      return currentStatus;
    }

    if (!currentStatus.canRequest) {
      return currentStatus;
    }

    const result = await this.performPermissionRequest(type);
    this.permissionCache.set(type, result);
    return result;
  }

  async checkMultiplePermissions(types: PermissionType[]): Promise<Map<PermissionType, PermissionResult>> {
    const results = new Map<PermissionType, PermissionResult>();
    
    const promises = types.map(async (type) => {
      const result = await this.checkPermission(type);
      results.set(type, result);
    });

    await Promise.all(promises);
    return results;
  }

  async requestMultiplePermissions(types: PermissionType[]): Promise<Map<PermissionType, PermissionResult>> {
    const results = new Map<PermissionType, PermissionResult>();
    
    // Solicita permissões sequencialmente para melhor UX
    for (const type of types) {
      const result = await this.requestPermission(type);
      results.set(type, result);
    }

    return results;
  }

  getCachedPermissions(): Map<PermissionType, PermissionResult> {
    return new Map(this.permissionCache);
  }

  clearCache(): void {
    this.permissionCache.clear();
  }

  private async queryPermission(type: PermissionType): Promise<PermissionResult> {
    switch (type) {
      case PermissionType.NOTIFICATIONS:
        return this.checkNotificationPermission();
      
      case PermissionType.GEOLOCATION:
        return await this.checkGeolocationPermission();
      
      case PermissionType.CAMERA:
      case PermissionType.MICROPHONE:
        return await this.checkMediaPermission(type);
      
      case PermissionType.CLIPBOARD_READ:
      case PermissionType.CLIPBOARD_WRITE:
        return await this.checkClipboardPermission(type);
      
      case PermissionType.FULLSCREEN:
        return this.checkFullscreenPermission();
      
      case PermissionType.PERSISTENT_STORAGE:
        return await this.checkPersistentStoragePermission();
      
      case PermissionType.BACKGROUND_SYNC:
      case PermissionType.PUSH:
        return await this.checkServiceWorkerPermission(type);
      
      default:
        return {
          status: PermissionStatus.UNSUPPORTED,
          canRequest: false,
          isSupported: false,
          timestamp: new Date()
        };
    }
  }

  private async performPermissionRequest(type: PermissionType): Promise<PermissionResult> {
    switch (type) {
      case PermissionType.NOTIFICATIONS:
        return await this.requestNotificationPermission();
      
      case PermissionType.GEOLOCATION:
        return await this.requestGeolocationPermission();
      
      case PermissionType.CAMERA:
      case PermissionType.MICROPHONE:
        return await this.requestMediaPermission(type);
      
      case PermissionType.CLIPBOARD_READ:
      case PermissionType.CLIPBOARD_WRITE:
        return await this.requestClipboardPermission(type);
      
      case PermissionType.PERSISTENT_STORAGE:
        return await this.requestPersistentStoragePermission();
      
      default:
        return await this.queryPermission(type);
    }
  }

  // Implementações específicas para cada tipo de permissão

  private checkNotificationPermission(): PermissionResult {
    if (!('Notification' in window)) {
      return {
        status: PermissionStatus.UNSUPPORTED,
        canRequest: false,
        isSupported: false,
        timestamp: new Date()
      };
    }

    const permission = Notification.permission;
    return {
      status: permission as PermissionStatus,
      canRequest: permission === 'default',
      isSupported: true,
      timestamp: new Date()
    };
  }

  private async requestNotificationPermission(): Promise<PermissionResult> {
    if (!('Notification' in window)) {
      return this.checkNotificationPermission();
    }

    try {
      const permission = await Notification.requestPermission();
      return {
        status: permission as PermissionStatus,
        canRequest: false,
        isSupported: true,
        timestamp: new Date()
      };
    } catch (error) {
      return {
        status: PermissionStatus.DENIED,
        canRequest: false,
        isSupported: true,
        timestamp: new Date()
      };
    }
  }

  private async checkGeolocationPermission(): Promise<PermissionResult> {
    if (!('geolocation' in navigator)) {
      return {
        status: PermissionStatus.UNSUPPORTED,
        canRequest: false,
        isSupported: false,
        timestamp: new Date()
      };
    }

    if ('permissions' in navigator) {
      try {
        const result = await navigator.permissions.query({ name: 'geolocation' });
        return {
          status: result.state as PermissionStatus,
          canRequest: result.state === 'prompt',
          isSupported: true,
          timestamp: new Date()
        };
      } catch {
        // Fallback para navegadores que não suportam permissions API
      }
    }

    return {
      status: PermissionStatus.PROMPT,
      canRequest: true,
      isSupported: true,
      timestamp: new Date()
    };
  }

  private async requestGeolocationPermission(): Promise<PermissionResult> {
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        () => resolve({
          status: PermissionStatus.GRANTED,
          canRequest: false,
          isSupported: true,
          timestamp: new Date()
        }),
        () => resolve({
          status: PermissionStatus.DENIED,
          canRequest: false,
          isSupported: true,
          timestamp: new Date()
        }),
        { timeout: 10000 }
      );
    });
  }

  private async checkMediaPermission(type: PermissionType): Promise<PermissionResult> {
    if (!('mediaDevices' in navigator) || !('getUserMedia' in navigator.mediaDevices)) {
      return {
        status: PermissionStatus.UNSUPPORTED,
        canRequest: false,
        isSupported: false,
        timestamp: new Date()
      };
    }

    if ('permissions' in navigator) {
      try {
        const permissionName = type === PermissionType.CAMERA ? 'camera' : 'microphone';
        const result = await navigator.permissions.query({ name: permissionName as any });
        return {
          status: result.state as PermissionStatus,
          canRequest: result.state === 'prompt',
          isSupported: true,
          timestamp: new Date()
        };
      } catch {
        // Fallback
      }
    }

    return {
      status: PermissionStatus.PROMPT,
      canRequest: true,
      isSupported: true,
      timestamp: new Date()
    };
  }

  private async requestMediaPermission(type: PermissionType): Promise<PermissionResult> {
    const constraints = type === PermissionType.CAMERA 
      ? { video: true } 
      : { audio: true };

    try {
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      stream.getTracks().forEach(track => track.stop()); // Limpa o stream
      
      return {
        status: PermissionStatus.GRANTED,
        canRequest: false,
        isSupported: true,
        timestamp: new Date()
      };
    } catch (error) {
      return {
        status: PermissionStatus.DENIED,
        canRequest: false,
        isSupported: true,
        timestamp: new Date()
      };
    }
  }

  private async checkClipboardPermission(type: PermissionType): Promise<PermissionResult> {
    if (!('clipboard' in navigator)) {
      return {
        status: PermissionStatus.UNSUPPORTED,
        canRequest: false,
        isSupported: false,
        timestamp: new Date()
      };
    }

    if ('permissions' in navigator) {
      try {
        const permissionName = type === PermissionType.CLIPBOARD_READ ? 'clipboard-read' : 'clipboard-write';
        const result = await navigator.permissions.query({ name: permissionName as any });
        return {
          status: result.state as PermissionStatus,
          canRequest: result.state === 'prompt',
          isSupported: true,
          timestamp: new Date()
        };
      } catch {
        // Fallback
      }
    }

    return {
      status: PermissionStatus.PROMPT,
      canRequest: true,
      isSupported: true,
      timestamp: new Date()
    };
  }

  private async requestClipboardPermission(type: PermissionType): Promise<PermissionResult> {
    try {
      if (type === PermissionType.CLIPBOARD_READ) {
        await navigator.clipboard.readText();
      } else {
        await navigator.clipboard.writeText('');
      }
      
      return {
        status: PermissionStatus.GRANTED,
        canRequest: false,
        isSupported: true,
        timestamp: new Date()
      };
    } catch (error) {
      return {
        status: PermissionStatus.DENIED,
        canRequest: false,
        isSupported: true,
        timestamp: new Date()
      };
    }
  }

  private checkFullscreenPermission(): PermissionResult {
    const isSupported = 'requestFullscreen' in document.documentElement;
    
    return {
      status: isSupported ? PermissionStatus.GRANTED : PermissionStatus.UNSUPPORTED,
      canRequest: false,
      isSupported,
      timestamp: new Date()
    };
  }

  private async checkPersistentStoragePermission(): Promise<PermissionResult> {
    if (!('storage' in navigator) || !('persist' in navigator.storage)) {
      return {
        status: PermissionStatus.UNSUPPORTED,
        canRequest: false,
        isSupported: false,
        timestamp: new Date()
      };
    }

    try {
      const isPersistent = await navigator.storage.persisted();
      return {
        status: isPersistent ? PermissionStatus.GRANTED : PermissionStatus.PROMPT,
        canRequest: !isPersistent,
        isSupported: true,
        timestamp: new Date()
      };
    } catch {
      return {
        status: PermissionStatus.UNSUPPORTED,
        canRequest: false,
        isSupported: false,
        timestamp: new Date()
      };
    }
  }

  private async requestPersistentStoragePermission(): Promise<PermissionResult> {
    try {
      const granted = await navigator.storage.persist();
      return {
        status: granted ? PermissionStatus.GRANTED : PermissionStatus.DENIED,
        canRequest: false,
        isSupported: true,
        timestamp: new Date()
      };
    } catch {
      return {
        status: PermissionStatus.DENIED,
        canRequest: false,
        isSupported: true,
        timestamp: new Date()
      };
    }
  }

  private async checkServiceWorkerPermission(type: PermissionType): Promise<PermissionResult> {
    if (!('serviceWorker' in navigator)) {
      return {
        status: PermissionStatus.UNSUPPORTED,
        canRequest: false,
        isSupported: false,
        timestamp: new Date()
      };
    }

    // Service Worker permissions são concedidas automaticamente em HTTPS
    const isSecure = location.protocol === 'https:' || location.hostname === 'localhost';
    
    return {
      status: isSecure ? PermissionStatus.GRANTED : PermissionStatus.DENIED,
      canRequest: false,
      isSupported: isSecure,
      timestamp: new Date()
    };
  }
}
